import { Pinia, Store } from '../pinia-class-component';
import type { QToolDevice } from '@/native/plugins/QTool';
import {
  type DMGScannedDevice,
  Dmgw6LteCheckData,
  type ScanState,
} from '@/models/installationPoint/InstallationPoint';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { submeteringDeviceTypes } from '@/enums/device';

export type GatewayFunkcheckEntry = {
  connectionStatus?: Dmgw6LteCheckData;
  status?: ScanState;
  devices: Map<string, DMGScannedDevice[]>;
};

export const Dmgw6DeviceFunkcheckStatus = {
  pending: 'pending',
  partial: 'partial',
  success: 'success',
  error: 'error',
  disconnected: 'disconnected',
} as const;
export type Dmgw6DeviceFunkcheckStatus =
  (typeof Dmgw6DeviceFunkcheckStatus)[keyof typeof Dmgw6DeviceFunkcheckStatus];

export function useDmgw6Store() {
  return new Dmgw6Store();
}

@Store
class Dmgw6Store extends Pinia {
  private _connectedQtool: QToolDevice | null;
  private _deviceFunkcheckStatuses: Map<string, Dmgw6DeviceFunkcheckStatus>;
  private _gatewayFunkcheckData: Map<string, GatewayFunkcheckEntry>;

  constructor() {
    super();
    this._connectedQtool = null;
    this._deviceFunkcheckStatuses = new Map();
    this._gatewayFunkcheckData = new Map();
  }

  get connectedQtool(): QToolDevice | null {
    return this._connectedQtool;
  }

  get deviceFunkcheckStatuses(): Map<string, Dmgw6DeviceFunkcheckStatus> {
    return this._deviceFunkcheckStatuses;
  }

  get hasFunkcheckStatuses(): boolean {
    return this._deviceFunkcheckStatuses.size > 0;
  }

  setConnectedQtool(device: QToolDevice) {
    this._connectedQtool = device;
  }

  clearConnectedQtool() {
    this._connectedQtool = null;
  }

  setDeviceFunkcheckStatus(
    deviceId: string,
    status: Dmgw6DeviceFunkcheckStatus
  ): void {
    if (this._deviceFunkcheckStatuses.get(deviceId) === status) return;
    const updated = new Map(this._deviceFunkcheckStatuses);
    updated.set(deviceId, status);
    this._deviceFunkcheckStatuses = updated;
  }

  bulkSetDeviceFunkcheckStatuses(
    updates: Map<string, Dmgw6DeviceFunkcheckStatus>
  ): void {
    let next: Map<string, Dmgw6DeviceFunkcheckStatus> | null = null;
    for (const [deviceId, status] of updates) {
      if (this._deviceFunkcheckStatuses.get(deviceId) === status) continue;
      if (!next) next = new Map(this._deviceFunkcheckStatuses);
      next.set(deviceId, status);
    }
    if (next) this._deviceFunkcheckStatuses = next;
  }

  clearFunkcheckStatuses(): void {
    this._deviceFunkcheckStatuses.clear();
  }

  getRoomFunkcheckStatus(
    roomId: string
  ): Dmgw6DeviceFunkcheckStatus | undefined {
    const devices = useDeviceStore()
      .getAllDevicesInRoom(roomId)
      .filter(
        (d) =>
          submeteringDeviceTypes.includes(d.deviceType) &&
          d.serialNumber &&
          d.isAutomatedMeterReading
      );
    if (devices.length === 0) return undefined;

    const statuses = devices
      .map((d) => this._deviceFunkcheckStatuses.get(d.id))
      .filter(Boolean) as Dmgw6DeviceFunkcheckStatus[];

    if (statuses.length === 0) return undefined;
    if (statuses.some((s) => s === Dmgw6DeviceFunkcheckStatus.error))
      return Dmgw6DeviceFunkcheckStatus.error;
    if (statuses.some((s) => s === Dmgw6DeviceFunkcheckStatus.pending))
      return Dmgw6DeviceFunkcheckStatus.pending;
    if (statuses.length < devices.length)
      return Dmgw6DeviceFunkcheckStatus.partial;
    if (statuses.some((s) => s === Dmgw6DeviceFunkcheckStatus.partial))
      return Dmgw6DeviceFunkcheckStatus.partial;
    if (statuses.every((s) => s === Dmgw6DeviceFunkcheckStatus.disconnected))
      return Dmgw6DeviceFunkcheckStatus.disconnected;
    return Dmgw6DeviceFunkcheckStatus.success;
  }

  get gatewayFunkcheckData(): Map<string, GatewayFunkcheckEntry> {
    return this._gatewayFunkcheckData;
  }

  setGatewayLteCheckData(serialNumber: string, data: Dmgw6LteCheckData): void {
    const entry = this.getOrCreateGatewayEntry(serialNumber);
    entry.connectionStatus = data;
    this.replaceGatewayEntry(serialNumber, entry);
  }

  setGatewayScanStatus(serialNumber: string, status: ScanState): void {
    const entry = this.getOrCreateGatewayEntry(serialNumber);
    entry.status = status;
    this.replaceGatewayEntry(serialNumber, entry);
  }

  upsertGatewayScannedDevice(
    gatewaySerial: string,
    deviceSerial: string,
    data: DMGScannedDevice
  ): void {
    const entry = this.getOrCreateGatewayEntry(gatewaySerial);
    const existing = entry.devices.get(deviceSerial) ?? [];
    const idx = existing.findIndex((e) => e.source === data.source);
    if (idx >= 0) existing[idx] = data;
    else existing.push(data);
    entry.devices.set(deviceSerial, existing);
    this.replaceGatewayEntry(gatewaySerial, entry);
  }

  bulkUpsertGatewayScannedDevices(
    gatewaySerial: string,
    devicesBySerial: Map<string, DMGScannedDevice[]>
  ): void {
    if (devicesBySerial.size === 0) return;
    const entry = this.getOrCreateGatewayEntry(gatewaySerial);
    for (const [deviceSerial, incoming] of devicesBySerial) {
      const existing = entry.devices.get(deviceSerial) ?? [];
      for (const data of incoming) {
        const idx = existing.findIndex((e) => e.source === data.source);
        if (idx >= 0) existing[idx] = data;
        else existing.push(data);
      }
      entry.devices.set(deviceSerial, existing);
    }
    this.replaceGatewayEntry(gatewaySerial, entry);
  }

  clearGatewayFunkcheckData(): void {
    this._gatewayFunkcheckData = new Map();
  }

  private getOrCreateGatewayEntry(serialNumber: string): GatewayFunkcheckEntry {
    return (
      this._gatewayFunkcheckData.get(serialNumber) ?? {
        devices: new Map(),
      }
    );
  }

  private replaceGatewayEntry(
    serialNumber: string,
    entry: GatewayFunkcheckEntry
  ): void {
    const updated = new Map(this._gatewayFunkcheckData);
    updated.set(serialNumber, entry);
    this._gatewayFunkcheckData = updated;
  }
}
